<app-modal
  [isOpen]="isOpen"
  (close)="onClose()"
  className="max-w-3xl mx-4 sm:mx-6"
>
  @if (company) {
    <div class="p-6 sm:p-8">
      <!-- Header with Avatar -->
      <div class="flex items-start gap-6 mb-8">
        <!-- Avatar Circle with Initials -->
        <div class="shrink-0">
          <div
            class="flex h-20 w-20 items-center justify-center rounded-full {{
              avatarColorClass
            }}"
          >
            <span class="text-2xl font-semibold">{{ initials }}</span>
          </div>
        </div>

        <!-- Name and Business Name -->
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {{ company.commercialName }}
          </h2>
          @if (company.businessName) {
            <p class="text-base text-gray-600 dark:text-gray-400">
              {{ company.businessName }}
            </p>
          }
        </div>

        <!-- responsable + quick actions -->
        <div class="mt-auto pt-4 flex flex-col items-end gap-2">
          <app-quick-actions-menu
            label="Acciones"
            align="right"
            [companyId]="company.id"
            [companyName]="company.commercialName"
            [contactPhone]="company.phone"
            (actionTriggered)="onClose()"
          >
          </app-quick-actions-menu>
          <div class="flex items-center gap-2 justify-end">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >Responsable:</span
            >
            @if (loadingUsers) {
              <div
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
              ></div>
            } @else if (ownerUser) {
              <span class="text-sm font-medium text-gray-900 dark:text-white">
                {{ ownerUser.fullname }} {{ ownerUser.lastname }}
              </span>
            } @else {
              <span class="text-sm text-gray-400 dark:text-gray-500">{{
                ownerLabel
              }}</span>
            }
          </div>
        </div>
      </div>

      <!-- Tab navigation -->
      <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav class="-mb-px flex gap-1">
          <button
            (click)="onTabChange('info')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'info'"
            [class.text-brand-600]="activeTab === 'info'"
            [class.dark:text-brand-400]="activeTab === 'info'"
            [class.border-transparent]="activeTab !== 'info'"
            [class.text-gray-500]="activeTab !== 'info'"
            [class.hover:text-gray-700]="activeTab !== 'info'"
            [class.dark:text-gray-400]="activeTab !== 'info'"
          >
            Información
          </button>
          <button
            (click)="onTabChange('timeline')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'timeline'"
            [class.text-brand-600]="activeTab === 'timeline'"
            [class.dark:text-brand-400]="activeTab === 'timeline'"
            [class.border-transparent]="activeTab !== 'timeline'"
            [class.text-gray-500]="activeTab !== 'timeline'"
            [class.hover:text-gray-700]="activeTab !== 'timeline'"
            [class.dark:text-gray-400]="activeTab !== 'timeline'"
          >
            Línea de Tiempo
          </button>
          <button
            (click)="onTabChange('automatizaciones')"
            class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            [class.border-brand-500]="activeTab === 'automatizaciones'"
            [class.text-brand-600]="activeTab === 'automatizaciones'"
            [class.dark:text-brand-400]="activeTab === 'automatizaciones'"
            [class.border-transparent]="activeTab !== 'automatizaciones'"
            [class.text-gray-500]="activeTab !== 'automatizaciones'"
            [class.hover:text-gray-700]="activeTab !== 'automatizaciones'"
            [class.dark:text-gray-400]="activeTab !== 'automatizaciones'"
          >
            Automatizaciones
          </button>
        </nav>
      </div>

      @if (activeTab === 'info') {
        <!-- Company Information Grid -->
        <div class="space-y-6">
          <!-- Company Contact Section -->
          <div>
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Información de Empresa
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Phone -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Teléfono
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ company.phone || '—' }}
                  </p>
                </div>
              </div>

              <!-- Website -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Sitio Web
                  </p>
                  <p
                    class="text-sm font-medium text-gray-900 dark:text-white break-all"
                  >
                    {{ company.website || '—' }}
                  </p>
                </div>
              </div>

              <!-- Fiscal Address -->
              <div class="flex items-start gap-3 sm:col-span-2">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Dirección Fiscal
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ company.fiscalAddress || '—' }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Fiscal and Classification Information Section -->
          <div>
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Información Fiscal y Clasificación
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Tax Type -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Tipo Fiscal
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ getTaxTypeName() }}
                  </p>
                </div>
              </div>

              <!-- Tax Code -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Código Fiscal
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ company.taxCode || '—' }}
                  </p>
                </div>
              </div>

              <!-- Relationship Type -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Tipo de Relación
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ getRelationshipTypeName() }}
                  </p>
                </div>
              </div>

              <!-- Size -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">Tamaño</p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ getSizeName() }}
                  </p>
                </div>
              </div>

              <!-- Sector -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">Sector</p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ getSectorName() }}
                  </p>
                </div>
              </div>

              <!-- State Lead -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">Estado</p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ getStateLeadName() }}
                  </p>
                </div>
              </div>

              <!-- Cycle -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">Ciclo</p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ company.companyCycleName || getCycleName() }}
                  </p>
                </div>
              </div>

              <!-- Business ID -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    ID Empresa/Tenant
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ company.businessId }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Description Section -->
          @if (company.description) {
            <div>
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
              >
                Descripción
              </h3>
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6h16M4 12h16M4 18h7"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p
                    class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap"
                  >
                    {{ company.description }}
                  </p>
                </div>
              </div>
            </div>
          }

          <!-- ======== Contactos Section ======== -->
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Contactos
            </h3>

            <!-- Loading state -->
            @if (loadingContacts) {
              <div class="flex items-center gap-3 py-4">
                <div
                  class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"
                ></div>
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Cargando contactos...</span
                >
              </div>
            }

            <!-- Error state -->
            @if (contactsError) {
              <div
                class="flex items-center gap-2 py-3 text-sm text-error-600 dark:text-error-400"
              >
                <svg
                  class="w-4 h-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                {{ contactsError }}
              </div>
            }

            <!-- Empty state -->
            @if (!loadingContacts && !contactsError && contacts.length === 0) {
              <div class="text-center py-6 text-gray-400 dark:text-gray-500">
                <svg
                  class="w-8 h-8 mx-auto mb-2 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
                <p class="text-sm">
                  No hay contactos asociados a esta empresa.
                </p>
              </div>
            }

            <!-- Contacts list -->
            @if (!loadingContacts && contacts.length > 0) {
              <div class="space-y-3">
                @for (contact of contacts; track contact.id) {
                  <div
                    class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50"
                  >
                    <!-- Avatar -->
                    <div
                      class="shrink-0 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold {{
                        getContactAvatarColor(contact)
                      }}"
                    >
                      {{ getContactInitials(contact) }}
                    </div>

                    <!-- Contact details -->
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span
                          class="text-sm font-medium text-gray-900 dark:text-white"
                        >
                          {{ contact.contactFullname
                          }}{{
                            contact.contactLastname
                              ? ' ' + contact.contactLastname
                              : ''
                          }}
                        </span>
                        @if (contact.isPrimary) {
                          <span
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                          >
                            <svg
                              class="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                              />
                            </svg>
                            Principal
                          </span>
                        }
                      </div>
                      <div class="flex items-center gap-3 mt-0.5 flex-wrap">
                        @if (contact.contactMail) {
                          <span
                            class="text-xs text-gray-500 dark:text-gray-400"
                            >{{ contact.contactMail }}</span
                          >
                        }
                        @if (contact.contactPhone) {
                          <span class="text-xs text-gray-400 dark:text-gray-500"
                            >·</span
                          >
                          <span
                            class="text-xs text-gray-500 dark:text-gray-400"
                            >{{ contact.contactPhone }}</span
                          >
                        }
                        @if (contact.position) {
                          <span class="text-xs text-gray-400 dark:text-gray-500"
                            >·</span
                          >
                          <span
                            class="text-xs text-gray-500 dark:text-gray-400 italic"
                            >{{ contact.position }}</span
                          >
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
          <!-- ======== Fin Contactos Section ======== -->

          <!-- Timestamps Section -->
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3"
            >
              Fechas
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Created Date -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Fecha de Creación
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ formatDate(company.datetimeCreated) }}
                  </p>
                </div>
              </div>

              <!-- Creado por -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Creado por
                  </p>
                  @if (loadingUsers) {
                    <div
                      class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 mt-1"
                    ></div>
                  } @else {
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {{ createdByLabel }}
                    </p>
                  }
                </div>
              </div>

              <!-- Updated Date -->
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                  <svg
                    class="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    ></path>
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Última Actualización
                  </p>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ formatDate(company.datetimeUpdated) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      <!-- end info tab -->

      <!-- ========== TAB: TIMELINE ========== -->
      @if (activeTab === 'timeline') {
        <app-audit-timeline
          [logs]="timelineLogs"
          [loading]="loadingTimeline"
          [error]="timelineError"
          [currentPage]="timelinePage"
          [totalPages]="timelineTotalPages"
          [totalElements]="timelineTotalElements"
          emptyMessage="No hay eventos de auditoría."
          (pageChange)="loadTimeline($event)"
        />
      }
      <!-- end timeline tab -->

      @if (activeTab === 'automatizaciones' && company.id) {
        <app-workflow-record-history
          objectType="COMPANY"
          [objectId]="company.id" />
      }
      <!-- end automatizaciones tab -->
    </div>
  }
</app-modal>
